import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';

const STORAGE_KEY = 'lanura_frosted_glass_enabled';

interface FrostedGlassContextType {
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
  toggle: () => void;
}

const FrostedGlassContext = createContext<FrostedGlassContextType | undefined>(undefined);

export const FrostedGlassProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [enabled, setEnabledState] = useState<boolean>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === null ? false : stored === 'true';
  });

  // Use ref to track enabled state to avoid stale closures
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  // Check if dark mode is enabled
  const isDarkMode = () => {
    return document.body.classList.contains('dark-mode-enabled');
  };

  const setEnabled = (value: boolean) => {
    // Don't enable frosted glass if dark mode is active
    if (value && isDarkMode()) {
      return;
    }
    setEnabledState(value);
    localStorage.setItem(STORAGE_KEY, value.toString());
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('frostedGlassChanged', { detail: { enabled: value } }));
    // Update body class for global styling
    if (value && !isDarkMode()) {
      document.body.classList.add('frosted-glass-enabled');
    } else {
      document.body.classList.remove('frosted-glass-enabled');
    }
  };

  const toggle = () => setEnabled(!enabled);

  // Initialize body class on mount and when enabled changes
  useEffect(() => {
    // Disable frosted glass if dark mode is active
    if (isDarkMode()) {
      document.body.classList.remove('frosted-glass-enabled');
      if (enabledRef.current) {
        setEnabledState(false);
      }
    } else if (enabledRef.current) {
      document.body.classList.add('frosted-glass-enabled');
    } else {
      document.body.classList.remove('frosted-glass-enabled');
    }
  }, [enabled]);

  // Listen for dark mode changes - removed enabled from dependencies to prevent loops
  useEffect(() => {
    let isHandling = false;

    const handleDarkModeChange = () => {
      // Prevent infinite loops
      if (isHandling) return;
      isHandling = true;

      if (isDarkMode()) {
        // Disable frosted glass when dark mode is enabled
        document.body.classList.remove('frosted-glass-enabled');
        if (enabledRef.current) {
          setEnabledState(false);
        }
      }

      // Reset flag after a short delay
      setTimeout(() => {
        isHandling = false;
      }, 100);
    };

    // Listen for custom events only (not MutationObserver to avoid loops)
    window.addEventListener('darkModeChanged', handleDarkModeChange);

    return () => {
      window.removeEventListener('darkModeChanged', handleDarkModeChange);
    };
  }, []); // Empty dependency array - only set up once

  // Listen for storage changes (e.g., from other tabs)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        const newValue = e.newValue === 'true';
        setEnabledState(newValue);
        if (newValue) {
          document.body.classList.add('frosted-glass-enabled');
        } else {
          document.body.classList.remove('frosted-glass-enabled');
        }
      }
    };

    const handleCustomEvent = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail?.enabled !== undefined) {
        setEnabledState(customEvent.detail.enabled);
        if (customEvent.detail.enabled) {
          document.body.classList.add('frosted-glass-enabled');
        } else {
          document.body.classList.remove('frosted-glass-enabled');
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('frostedGlassChanged', handleCustomEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('frostedGlassChanged', handleCustomEvent);
    };
  }, []);

  return (
    <FrostedGlassContext.Provider value={{ enabled, setEnabled, toggle }}>
      {children}
    </FrostedGlassContext.Provider>
  );
};

export const useFrostedGlass = () => {
  const context = useContext(FrostedGlassContext);
  if (context === undefined) {
    throw new Error('useFrostedGlass must be used within a FrostedGlassProvider');
  }
  return context;
};
